import { HttpClient, HttpHeaders, HttpErrorResponse } from "@angular/common/http";
import { IHttpRepo, HttpContentType, EncType } from "@shared/interfaces/IHttpRepo";
import { AppEndPointModel } from "@models/endpoint-url.model";
import { Injectable } from "@angular/core";
import { ApiEndPointType } from "@shared/enums/common";
import { AppConfig } from "@appconfig";
import { isDevMode } from "@angular/core";
import { catchError, timeout } from 'rxjs/operators';
import { DataSourceRequestState } from "@progress/kendo-data-query";
import { CommonMethods } from "@services/common.method";
import { of } from "rxjs";
@Injectable({
  providedIn: "root"
})
export class HttpRepo implements IHttpRepo {
  private absoluteUrl: string;
  private companyId: string;

  public get AbsoluteURL(): string {
    this.checkAbsoluteUrl();
    return this.absoluteUrl;
  }

  constructor(private http: HttpClient, private appConfig: AppConfig) { }

  /**
   * @remarks Set url to be used as Absolute URL
   * @param companyId
   * @param endPointType
   */
  setAbsoluteUrl(companyId: string, endPointType: ApiEndPointType) {
    this.companyId = companyId;
    this.absoluteUrl = this.getApiUrl(companyId, endPointType);
  }

  /**
   * @remarks get url using endpoint type
   * @param companyId
   * @param endPointType
   */
  public getApiUrl(companyId: string, endPointType: ApiEndPointType) {
    let endPointModel = new AppEndPointModel();
    let url = "";
    switch (endPointType) {
      case ApiEndPointType.FS:
        let apiUrl = endPointModel.FieldServiceEndpointPrefix;
        if (isDevMode) {
          apiUrl = this.appConfig.getApiUrl();
        }
        url = apiUrl + "/companies/" + companyId;
        break;
      case ApiEndPointType.IM:
        url = endPointModel.InventoryManagementEndPoint + "/" + companyId;
        break;
      case ApiEndPointType.QB:
        url = endPointModel.QuickBookOnlineEndPoint + "/" + companyId;
        break;
      case ApiEndPointType.Payment:
        url = endPointModel.PaymentProcessorCardConnectEndPoint + "/" + companyId;
        break;
    }
    return url;
  }

  /**
   * @remarks Get request using company url
   * @param relativeUrl
   * @param endPointType
   */
  Get(relativeUrl: string, queryParams: string = "", endPointType?: ApiEndPointType) {
    this.checkAbsoluteUrl();
    let url = this.absoluteUrl;
    if (endPointType != null) {
      url = this.getApiUrl(this.companyId, endPointType);
    }
    if (!CommonMethods.CheckNullOrUndefined(queryParams, true))
      queryParams = "?" + queryParams;

    return this.http.get<any>(url + "/" + relativeUrl + queryParams).pipe(catchError(this.handleErrors));
  }

  /**
   * @remarks Get request using company url to download file
   * @param relativeUrl
   * @param endPointType
   */
  GetFile(relativeUrl: string, queryParams: string = "", endPointType?: ApiEndPointType) {
    this.checkAbsoluteUrl();
    let url = this.absoluteUrl;
    if (endPointType != null) {
      url = this.getApiUrl(this.companyId, endPointType);
    }
    if (!CommonMethods.CheckNullOrUndefined(queryParams, true))
      queryParams = "?" + queryParams;

    return this.http.get(url + "/" + relativeUrl + queryParams,{ responseType:'blob' }).pipe(catchError(this.handleErrors));
  }



  /**
   * @remarks Get request using company url
   * @param relativeUrl
   * @param endPointType
   */
  GetPeriodic(relativeUrl: string, modifySinceDate?: string, endPointType?: ApiEndPointType): any {
    this.checkAbsoluteUrl();
    let url = this.absoluteUrl;
    if (endPointType != null) {
      url = this.getApiUrl(this.companyId, endPointType);
    }
    let modifySinceHeader = new HttpHeaders({ "If-Modified-Since": modifySinceDate });
    return this.http.get<any>(url + "/" + relativeUrl, {
      headers: modifySinceHeader
    }).pipe(catchError(this.handleErrors));
  }

  /**
   * @remarks Get request using absolute Url
   * @param url
   */
  GetByAbsURL(url: string) {
    return this.http.get<any>(url).pipe(catchError(this.handleErrors));
  }

  /**
   * @remarks Post request using absolute Url
   * @param url
   * @param data
   */
  PostToAbsURL(url: string, data: any) {
    return this.http.post<any>(url, data).pipe(catchError(this.handleErrors));
  }

  /**
   * @remarks Get References using company url
   * @param qstring
   * @param endPointType
   */
  GetReferences(qstring: string, endPointType?: ApiEndPointType) {
    this.checkAbsoluteUrl();
    let url = this.absoluteUrl;
    if (endPointType != null) {
      url = this.getApiUrl(this.companyId, endPointType);
    }
    return this.http.get<any>(url + qstring).pipe(catchError(this.handleErrors));
  }

  /**
   * @remarks Put using company url
   * @param relativeUrl
   * @param data
   * @param endPointType
   */
  Put(relativeUrl: string, data: any, endPointType?: ApiEndPointType, forCompany: boolean = false) {
    this.checkAbsoluteUrl();
    let url = this.absoluteUrl;
    if (endPointType != null) {
      url = this.getApiUrl(this.companyId, endPointType);
    }
    if (forCompany)
      return this.http.put<any>(url, data).pipe(catchError(this.handleErrors));
    else
      return this.http.put<any>(url + "/" + relativeUrl, data).pipe(catchError(this.handleErrors));
  }

  /**
   * @remarks Post using company url
   * @param relativeUrl
   * @param data
   * @param endPointType
   */
  Post(relativeUrl: string, data: any, endPointType?: ApiEndPointType) {
    this.checkAbsoluteUrl();
    let url = this.absoluteUrl;
    if (endPointType != null) {
      url = this.getApiUrl(this.companyId, endPointType);
    }
    return this.http.post<any>(url + "/" + relativeUrl, data, {
      headers: HttpContentType.JSON
    }).pipe(catchError(this.handleErrors));
  }

  /**
   * @remarks Patch using company url
   * @param relativeUrl
   * @param data
   * @param endPointType
   */
  Patch(relativeUrl: string, data: any, endPointType?: ApiEndPointType) {
    this.checkAbsoluteUrl();
    let url = this.absoluteUrl;
    if (endPointType != null) {
      url = this.getApiUrl(this.companyId, endPointType);
    }
    return this.http.patch<any>(url + "/" + relativeUrl, data).pipe(catchError(this.handleErrors));
  }

  /**
   * @remarks Delete using company url
   * @param relativeUrl
   * @param endPointType
   */
  Delete(relativeUrl: string, endPointType?: ApiEndPointType) {
    this.checkAbsoluteUrl();
    let url = this.absoluteUrl;
    if (endPointType != null) {
      url = this.getApiUrl(this.companyId, endPointType);
    }
    return this.http.delete<any>(url + "/" + relativeUrl).pipe(catchError(this.handleErrors));
  }

  PostFile(relativeUrl: string, data: any, endPointType?: ApiEndPointType, forCompany: boolean = false) {
    this.checkAbsoluteUrl();
    let url = this.absoluteUrl;
    if (endPointType != null) {
      url = this.getApiUrl(this.companyId, endPointType);
    }
    if (forCompany) {
      return this.http.post<any>(url, data, {
        headers: EncType.multiPartFormData
      }).pipe(catchError(this.handleErrors));
    }
    else
      return this.http.post<any>(url + "/" + relativeUrl, data, {
        headers: EncType.multiPartFormData
      }).pipe(catchError(this.handleErrors));
  }

  public GetCommonURLQueryParams(state: DataSourceRequestState, searchKey: string = "", references: string = null) {
    if (state.filter) {
      state.filter.filters.forEach(x => {
        if (x.hasOwnProperty("value2")) {
          delete x["value2"];
        }
      });
    }
    
    let url = "take=" + (state.take == undefined ? "" : state.take) + "&skip=" + (state.skip == undefined ? "" : state.skip) + "&sort=" + ((state.sort != undefined && state.sort.length > 0) ? JSON.stringify(state.sort) : "") + "&filter=" + ((state.filter != undefined && state.filter.filters.length > 0) ? encodeURIComponent(JSON.stringify(state.filter.filters)) : "") + "&searchKey=" + encodeURIComponent(searchKey.trim());
    if (references != null) url += "&references="+references;
    return url;
  }

   /**
   * @remarks Get request using absolute Url
   * @param url
   */
  public GetQbExtractorUrlWithHeaders(url: string, token: string) {
    let httpOptions = new HttpHeaders();
    token = `Bearer ${token}`
    httpOptions = httpOptions.set('Authorization', token);
    return this.http.get<any>(url, { headers: httpOptions }).pipe(timeout(300 * 1000), catchError(e => { return of(null); }));
  }   

  /**
   * @remarks To check if Absolute Url is set or not
   */
  private checkAbsoluteUrl() {
    if (
      this.absoluteUrl == undefined ||
      this.absoluteUrl == null ||
      this.absoluteUrl.trim() == ""
    ) {
      throw "Absolute Url is not set for the 'HttpRepo' service. Call the Method 'setAbsoluteUrl' of 'HttpRepo' service to set Absolute Url";
    }
  }
  private handleErrors(error: HttpErrorResponse) {
    if (error.status == 400) // bad request
    {
      //let testingerror = "{\"City\":[\"'City' must not be empty.\"],\"Type\":[\"'Type' must not be empty.\"],\"State\":[\"'State' must not be empty.\"],\"ZipCode\":[\"'Zip Code' must not be empty.\"],\"JobSites\":[\"'Job Sites' must not be empty.\"],\"DisplayName\":[\"Customer display name katla, ashok already exists and must be unique.\"],\"PaymentTerm\":[\"'Payment Term' must not be empty.\"],\"StreetAddress\":[\"'Street Address' must not be empty.\"]}";
      let errorsJSON = JSON.parse(error.error.Message);
      let errorString = "";
      Object.keys(errorsJSON).forEach(function (key, index) {
        errorsJSON[key].forEach(errorMessage => {
          errorMessage = errorMessage.replace(/(\r\n\r\n|\n\n|\r\r)/gm, ""); /* As some message from server have \\r\\n\ and some not */
          errorString += errorMessage + "\r\n";
        });
      });
      error.error.Message = errorString;
      return Promise.reject(error);
    }
    return Promise.reject(error);
  }
}
